import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Pages
import AdminLoginPage from './pages/AdminLoginPage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import AdminAnalyticsPage from './pages/AdminAnalyticsPage'
import UserManagementPage from './pages/UserManagementPage'
import UserDetailPage from './pages/UserDetailPage'
import LinkManagementPage from './pages/LinkManagementPage'
import SecuritySettingsPage from './pages/SecuritySettingsPage'
import SecurityLogsPage from './pages/SecurityLogsPage'
import EmailManagementPage from './pages/EmailManagementPage'
import PlanManagementPage from './pages/PlanManagementPage'
import BackupManagementPage from './pages/BackupManagementPage'
import CapacityManagementPage from './pages/CapacityManagementPage'
import SubscriptionManagementPage from './pages/SubscriptionManagementPage'
import ApiManagementPage from './pages/ApiManagementPage'
import PlanHistoryPage from './pages/PlanHistoryPage'
import VersionManagementPage from './pages/VersionManagementPage'
import NotFoundPage from '../../shared/pages/NotFoundPage'

// Components
import AdminProtectedRoute from '../../shared/components/AdminProtectedRoute'

// Hooks
import { useTheme } from '../../shared/hooks/useTheme'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

function AdminApp() {
  const { resolvedTheme, initializeTheme } = useTheme()

  useEffect(() => {
    initializeTheme()
  }, [initializeTheme])

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
          <Routes>
            <Route path="/login" element={<AdminLoginPage />} />
            <Route 
              path="/" 
              element={
                <AdminProtectedRoute>
                  <AdminDashboardPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <AdminProtectedRoute>
                  <AdminDashboardPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/users" 
              element={
                <AdminProtectedRoute>
                  <UserManagementPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/users/:userId" 
              element={
                <AdminProtectedRoute>
                  <UserDetailPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/links" 
              element={
                <AdminProtectedRoute>
                  <LinkManagementPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/analytics" 
              element={
                <AdminProtectedRoute>
                  <AdminAnalyticsPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/security" 
              element={
                <AdminProtectedRoute>
                  <SecuritySettingsPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/security/logs" 
              element={
                <AdminProtectedRoute>
                  <SecurityLogsPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/email" 
              element={
                <AdminProtectedRoute>
                  <EmailManagementPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/plans" 
              element={
                <AdminProtectedRoute>
                  <PlanManagementPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/plan-history" 
              element={
                <AdminProtectedRoute>
                  <PlanHistoryPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/backup" 
              element={
                <AdminProtectedRoute>
                  <BackupManagementPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/capacity" 
              element={
                <AdminProtectedRoute>
                  <CapacityManagementPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/subscriptions" 
              element={
                <AdminProtectedRoute>
                  <SubscriptionManagementPage />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/api-management" 
              element={
                <AdminProtectedRoute>
                  <ApiManagementPage />
                </AdminProtectedRoute>
              } 
            />
            <Route
              path="/version"
              element={
                <AdminProtectedRoute>
                  <VersionManagementPage />
                </AdminProtectedRoute>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          
          {/* Toast Notifications */}
          <Toaster
            position="bottom-right"
            reverseOrder={false}
            gutter={8}
            containerClassName=""
            containerStyle={{}}
            toastOptions={{
              className: '',
              duration: 4000,
              style: {
                background: resolvedTheme === 'dark' ? '#374151' : '#ffffff',
                color: resolvedTheme === 'dark' ? '#f9fafb' : '#111827',
                border: resolvedTheme === 'dark' ? '1px solid #4b5563' : '1px solid #e5e7eb',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#ffffff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
            }}
          />
        </div>
      </Router>
    </QueryClientProvider>
  )
}

export default AdminApp